import { User } from "@ecologi/shared";
import { StripeCardNumberElement } from "@stripe/stripe-js";
import { useCallback } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import {
  APIError,
  formatAPIErrorForView,
  setupSubscriptionIntent,
  updatePaymentMethodDetails,
} from "../../../lib";
import { useStore } from "../../../store";

export const useAddCardDetails = () => {
  const { user, stripe, setUser } = useStore();

  const addCardDetails = useCallback(
    async ({
      cardElement,
      captchaToken,
    }: {
      cardElement: StripeCardNumberElement;
      captchaToken: string;
    }) => {
      const name = `${user.firstName.trim()} ${user.lastName.trim()}`.trim();

      // Create a payment card method object with Stripe
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name,
          email: user.billingEmail || user.email,
        },
      });

      if (error || !paymentMethod) {
        throw new Error(
          "Either your billing address or card details are wrong. Please double check them and try again."
        );
      }

      const setupIntent = await setupSubscriptionIntent({
        captchaToken,
        paymentMethodId: paymentMethod.id,
      });

      // If there is action that needs to happen (ie. 3DS confirmation),
      // use Stripe SDK to ask for it
      if (setupIntent.requiresAction) {
        const cardConfirmationRes = await stripe.confirmCardSetup(
          setupIntent.clientSecret
        );

        if (cardConfirmationRes.error) {
          throw new Error(cardConfirmationRes.error.message);
        }

        if (cardConfirmationRes.setupIntent?.status !== "succeeded") {
          throw new Error(
            "Card verification failed. Please try again or use a different card."
          );
        }
      }

      return updatePaymentMethodDetails({
        paymentMethodId: paymentMethod.id,
      });
    },
    [user, stripe]
  );
  const [addCardDetailsMutation] = useMutation(addCardDetails, {
    onSuccess: (newUser) => {
      setUser(new User(newUser));
      /* Toast messages to be removed in favour of feedback from textfields/form itself */
      toast.success("Payment details added.");
      window.scrollTo(0, 0);
    },
    onError(error: APIError) {
      /* Toast messages to be removed in favour of feedback from textfields/form itself */
      toast.error(
        "An error occurred adding your payment details. Please try again. If the issue persists contact support."
      );
    },
  });
  return addCardDetailsMutation;
};
